import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Chip,
  Alert,
  Collapse,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  Check as CheckIcon,
  Edit as EditIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';

interface Field {
  id: string;
  placeholder: string;
  value: string;
  required: boolean;
  filled: boolean;
}

interface FieldsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  documentContent: string;
  onFieldUpdate: (fields: Record<string, string>) => void;
}

const FieldsPanel: React.FC<FieldsPanelProps> = ({
  isOpen,
  onClose,
  documentContent,
  onFieldUpdate
}) => {
  const [fields, setFields] = useState<Field[]>([]);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});

  // Detectar campos no documento
  useEffect(() => {
    if (isOpen && documentContent) {
      detectFields();
    }
  }, [isOpen, documentContent]);

  const detectFields = () => {
    // Procurar padrões [campo] no documento
    const fieldPattern = /\[([^\]]+)\]/g;
    const foundFields = new Set<string>();
    let match;

    while ((match = fieldPattern.exec(documentContent)) !== null) {
      const fieldName = match[1].trim();
      if (fieldName && !foundFields.has(fieldName)) {
        foundFields.add(fieldName);
      }
    }

    const fieldsList: Field[] = Array.from(foundFields).map((fieldName, index) => ({
      id: `field-${index}`,
      placeholder: fieldName,
      value: fieldValues[fieldName] || '',
      required: true, // Assumir que todos são obrigatórios
      filled: Boolean(fieldValues[fieldName]?.trim())
    }));

    setFields(fieldsList);
  };

  const handleFieldChange = (fieldPlaceholder: string, value: string) => {
    setFieldValues(prev => ({
      ...prev,
      [fieldPlaceholder]: value
    }));

    // Atualizar status do campo
    setFields(prev => prev.map(field => 
      field.placeholder === fieldPlaceholder 
        ? { ...field, value, filled: Boolean(value.trim()) }
        : field
    ));
  };

  const applyFields = () => {
    onFieldUpdate(fieldValues);
    onClose();
  };

  const getCompletionStats = () => {
    const total = fields.length;
    const completed = fields.filter(f => f.filled).length;
    return { total, completed, percentage: total > 0 ? (completed / total) * 100 : 0 };
  };

  const stats = getCompletionStats();

  if (!isOpen) return null;

  return (
    <Paper
      sx={{
        position: 'fixed',
        top: 64,
        right: 0,
        width: 400,
        height: 'calc(100vh - 64px)',
        zIndex: 1200,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 0,
        borderLeft: 1,
        borderColor: 'divider'
      }}
    >
      {/* Header */}
      <Box sx={{ 
        p: 2, 
        borderBottom: 1, 
        borderColor: 'divider',
        backgroundColor: '#f8f9fa'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssignmentIcon color="primary" />
            <Typography variant="h6">
              Campos do Documento
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        
        {/* Status de preenchimento */}
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Progresso: {stats.completed}/{stats.total} campos
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {Math.round(stats.percentage)}%
            </Typography>
          </Box>
          <Box sx={{
            width: '100%',
            height: 4,
            backgroundColor: '#e0e0e0',
            borderRadius: 2,
            overflow: 'hidden'
          }}>
            <Box sx={{
              width: `${stats.percentage}%`,
              height: '100%',
              backgroundColor: stats.percentage === 100 ? '#4caf50' : '#1976d2',
              transition: 'width 0.3s ease'
            }} />
          </Box>
        </Box>
      </Box>

      {/* Lista de campos */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
        {fields.length === 0 ? (
          <Alert severity="info" sx={{ m: 2 }}>
            Nenhum campo [texto] encontrado no documento
          </Alert>
        ) : (
          <List dense>
            {fields.map((field) => (
              <ListItem
                key={field.id}
                sx={{
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  mb: 1,
                  backgroundColor: field.filled ? '#f0f8ff' : '#fff3e0',
                  borderRadius: 1,
                  border: 1,
                  borderColor: field.filled ? '#1976d2' : '#ff9800'
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  width: '100%',
                  mb: 1
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    [{field.placeholder}]
                  </Typography>
                  <Chip
                    size="small"
                    icon={field.filled ? <CheckCircleIcon /> : <WarningIcon />}
                    label={field.filled ? 'Preenchido' : 'Pendente'}
                    color={field.filled ? 'success' : 'warning'}
                    variant="outlined"
                  />
                </Box>
                
                <TextField
                  fullWidth
                  size="small"
                  placeholder={`Digite o valor para ${field.placeholder}`}
                  value={field.value}
                  onChange={(e) => handleFieldChange(field.placeholder, e.target.value)}
                  variant="outlined"
                  multiline
                  maxRows={3}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      {/* Footer com ações */}
      <Box sx={{ 
        p: 2, 
        borderTop: 1, 
        borderColor: 'divider',
        backgroundColor: '#f8f9fa'
      }}>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={applyFields}
          disabled={fields.length === 0}
          startIcon={<CheckIcon />}
          sx={{ mb: 1 }}
        >
          Aplicar Campos ({stats.completed}/{stats.total})
        </Button>
        
        <Button
          fullWidth
          variant="outlined"
          onClick={detectFields}
          startIcon={<AssignmentIcon />}
        >
          Redetectar Campos
        </Button>
      </Box>
    </Paper>
  );
};

export default FieldsPanel;
